import React from 'react';

export function HomeUniLoader({ size = 'md', fullPage = false, message }) {
  const logoW = size === 'sm' ? '100px' : size === 'lg' ? '200px' : '140px';
  const spinnerSize = size === 'sm' ? 56 : size === 'lg' ? 110 : 80;
  const stroke = size === 'sm' ? 2.5 : 3;

  const inner = (
    <div className="flex flex-col items-center gap-4" data-testid="homeuni-loader">
      {/* Spinner ring + logo stacked */}
      <div className="relative flex items-center justify-center" style={{ width: spinnerSize, height: spinnerSize }}>
        {/* Outer glow ring */}
        <svg
          className="absolute inset-0"
          width={spinnerSize} height={spinnerSize}
          viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}
          fill="none"
          style={{ animation: 'hu-spin 1.6s linear infinite' }}
        >
          <circle
            cx={spinnerSize / 2} cy={spinnerSize / 2}
            r={spinnerSize / 2 - stroke}
            stroke="#42A5F5"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${spinnerSize * 1.4} ${spinnerSize * 2.2}`}
            opacity="0.9"
          />
        </svg>
        {/* Inner counter-spin subtle ring */}
        <svg
          className="absolute inset-0"
          width={spinnerSize} height={spinnerSize}
          viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}
          fill="none"
          style={{ animation: 'hu-spin-rev 3s linear infinite' }}
        >
          <circle
            cx={spinnerSize / 2} cy={spinnerSize / 2}
            r={spinnerSize / 2 - stroke * 3.5}
            stroke="#1565C0"
            strokeWidth={stroke * 0.6}
            strokeLinecap="round"
            strokeDasharray={`${spinnerSize * 0.5} ${spinnerSize * 3}`}
            opacity="0.3"
          />
        </svg>
        {/* Logo mark in center */}
        <div className="w-8 h-8 rounded-xl bg-[#1565C0]/8 flex items-center justify-center">
          <img src="/main-logo-horinzontal.png" alt="" className="w-6 h-6 object-contain opacity-70" />
        </div>
      </div>

      {/* Dots */}
      <div className="flex gap-1.5">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: i === 1 ? '#42A5F5' : '#1565C0',
              opacity: i === 1 ? 0.9 : 0.35,
              animation: `hu-dot-bounce 1.2s ease-in-out infinite ${i * 0.18}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes hu-spin { to { transform: rotate(360deg); } }
        @keyframes hu-spin-rev { to { transform: rotate(-360deg); } }
        @keyframes hu-dot-bounce { 0%,100%{transform:translateY(0);opacity:.35} 50%{transform:translateY(-4px);opacity:1} }
      `}</style>
    </div>
  );

  if (fullPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle, #1565C0 0.8px, transparent 0.8px)',
            backgroundSize: '36px 36px',
            opacity: 0.03,
          }}
        />

        {/* Large soft glow blobs */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #42A5F515, transparent)', transform: 'translate(-40%, -40%)' }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #1565C010, transparent)', transform: 'translate(40%, 40%)' }} />

        {/* Floating accent lines */}
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: `${180 + i * 120}px`,
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${i % 2 === 0 ? '#42A5F5' : '#1565C0'}22, transparent)`,
              top: `${30 + i * 25}%`,
              left: `${10 + i * 15}%`,
              animation: `hu-line-drift ${5 + i * 1.5}s ease-in-out infinite ${i * 0.8}s`,
            }}
          />
        ))}

        <div className="relative flex flex-col items-center gap-6">
          {/* Main logo — large, natural colors */}
          <img
            src="/main-logo-horinzontal.png"
            alt="HomeUni"
            style={{ width: '220px', height: 'auto', animation: 'hu-logo-appear 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards' }}
            className="object-contain"
          />

          {/* Spinner + dots inline */}
          <div className="flex flex-col items-center gap-3">
            {/* Progress bar */}
            <div className="w-48 h-[2px] bg-[#1565C0]/08 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #1565C0, #42A5F5)',
                  animation: 'hu-progress-bar 2s ease-in-out infinite',
                }}
              />
            </div>

            {/* Spinning arc */}
            <svg
              width="32" height="32" viewBox="0 0 32 32" fill="none"
              style={{ animation: 'hu-spin 1.4s linear infinite' }}
            >
              <circle cx="16" cy="16" r="13" stroke="#e8e5d8" strokeWidth="2.5" />
              <circle cx="16" cy="16" r="13" stroke="#42A5F5" strokeWidth="2.5"
                strokeLinecap="round" strokeDasharray="28 54" />
            </svg>

            <p
              className="text-[11px] font-medium tracking-[0.22em] uppercase"
              style={{ color: '#1565C0', opacity: 0.4, animation: 'hu-fade-pulse 2s ease-in-out infinite' }}
            >
              {message || 'Loading your workspace'}
            </p>
          </div>
        </div>

        <style>{`
          @keyframes hu-spin { to { transform: rotate(360deg); } }
          @keyframes hu-progress-bar { 0%{width:0%;margin-left:0} 45%{width:60%} 55%{width:60%} 100%{width:0%;margin-left:100%} }
          @keyframes hu-fade-pulse { 0%,100%{opacity:.4} 50%{opacity:.7} }
          @keyframes hu-logo-appear { from{opacity:0;transform:translateY(12px) scale(0.95)} to{opacity:1;transform:translateY(0) scale(1)} }
          @keyframes hu-line-drift { 0%,100%{transform:translateX(0);opacity:0.6} 50%{transform:translateX(30px);opacity:1} }
        `}</style>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-12">
      {inner}
    </div>
  );
}

export default HomeUniLoader;
